import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Box, Button, Tooltip } from "@mui/material";
import React from "react";
import { useSlidingAppearance } from "../lib/AppearanceContext";

/**
 * iOS-style segmented control (pill in filled track).
 * @param {{ value: string|number, label: string, disabled?: boolean, locked?: boolean, tooltip?: string }[]} options
 */
export default function SegmentedControl({
    options,
    value,
    onChange,
    disabled = false,
    "aria-label": ariaLabel,
    /** When true, expose tab semantics for screen readers. */
    asTabs = false,
}) {
    const { tokens } = useSlidingAppearance();
    const compact = options.length >= 3;

    return (
        <Box
            role={asTabs ? "tablist" : "group"}
            aria-label={ariaLabel}
            sx={{
                display: "flex",
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
                p: 0.375,
                borderRadius: "10px",
                bgcolor: tokens.segmentTrackBg,
                border: `1px solid ${tokens.segmentTrackBorder}`,
                overflow: "hidden",
            }}
        >
            {options.map((opt) => {
                const selected = value === opt.value;
                const isDisabled = disabled || opt.disabled;
                const button = (
                    <Button
                        key={String(opt.value)}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => {
                            if (!isDisabled && !selected) onChange(opt.value);
                        }}
                        role={asTabs ? "tab" : undefined}
                        aria-selected={asTabs ? selected : undefined}
                        aria-pressed={asTabs ? undefined : selected}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            minHeight: 36,
                            py: 0.75,
                            px: compact ? 0.5 : 1,
                            borderRadius: "8px",
                            textTransform: "none",
                            fontSize: compact ? "0.8125rem" : "0.875rem",
                            fontWeight: selected ? 600 : 500,
                            fontFamily:
                                '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", sans-serif',
                            color: selected ? tokens.segmentSelectedText : tokens.segmentInactiveText,
                            bgcolor: selected ? tokens.segmentSelectedBg : "transparent",
                            boxShadow: selected
                                ? "0 1px 4px rgba(0, 0, 0, 0.18), 0 0 1px rgba(0, 0, 0, 0.08)"
                                : "none",
                            transition: "background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease",
                            whiteSpace: "nowrap",
                            "&:hover": {
                                bgcolor: selected
                                    ? tokens.segmentSelectedBg
                                    : "rgba(59, 166, 102, 0.08)",
                            },
                            "&.Mui-disabled": {
                                color: tokens.textSubtle,
                            },
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.35,
                                minWidth: 0,
                                maxWidth: "100%",
                            }}
                        >
                            <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                                {opt.label}
                            </Box>
                            {opt.locked ? (
                                <LockOutlinedIcon
                                    sx={{
                                        fontSize: compact ? 13 : 14,
                                        flexShrink: 0,
                                        opacity: 0.55,
                                    }}
                                    aria-hidden
                                />
                            ) : null}
                        </Box>
                    </Button>
                );

                if (opt.tooltip && opt.disabled) {
                    return (
                        <Tooltip key={String(opt.value)} title={opt.tooltip} arrow placement="top">
                            <Box component="span" sx={{ display: "flex", flex: 1, minWidth: 0 }}>
                                {button}
                            </Box>
                        </Tooltip>
                    );
                }

                return button;
            })}
        </Box>
    );
}
